/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Flex,
  Icon,
  Scrollbar,
  Spinner,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { AngleDownIcon, AngleRightIcon } from '@tonic-one/react-icons';
import { Tree } from 'antd';
import type { AntTreeNodeProps, DataNode, TreeProps } from 'antd/es/tree';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import config from 'src/config';
import { AllFacets, SearchItem } from 'src/types/log';
import { urlMask } from 'src/utils/common';

import { useOrgStore } from '@Models/useOrgModel';
import { t } from 'i18next';
import TextEllipsis from '../../../components/Common/Tooltip/TextEllipsis';
import { mappingFields, urlMaskFields } from '../constants';
import { useLogFacetStyle } from './style';

const getMappingFieldsName = (facetName: string, key: string) => {
  const l10nkey = `quarantine.${facetName}.${key}`;
  const showName = t(l10nkey);
  return showName === l10nkey ? key : showName;
};

interface ViewMoreStatus {
  [key: string]: number;
}
const DELIMITER = '@$@';
const INTERVAL = config.ui.logDefaultValue.debounceInterval;

function MainFacetTitle({ title }: { title: string }) {
  const { mainFacetTitle } = useLogFacetStyle();
  return (
    <Flex justify="space-between" mr={2}>
      <Box style={mainFacetTitle}>
        <TextEllipsis placement="top">{title}</TextEllipsis>
      </Box>
    </Flex>
  );
}
function SubFacetTitle({ title, count }: { title: string; count: number }) {
  const { subFacetTitle } = useLogFacetStyle();
  return (
    <Flex justify="space-between" mr={2} width="200px">
      <Box style={subFacetTitle}>
        <TextEllipsis placement="top">{title}</TextEllipsis>
      </Box>
      <Box textAlign="right">
        <TextEllipsis placement="top">{count.toString()}</TextEllipsis>
      </Box>
    </Flex>
  );
}

const viewMoreNode = (
  facet: string,
  index: number,
  title: string
): DataNode => {
  const node: DataNode = {
    title: (
      <Box color="#6F9BF4">
        {title}
        <Icon as={AngleDownIcon} />
      </Box>
    ),
    key: `${facet}${DELIMITER}${index}`,
    checkable: false,
  };
  return node;
};

const SwitchIcon = (props: AntTreeNodeProps) => {
  return props.expanded ? (
    <Icon as={AngleDownIcon} />
  ) : (
    <Icon as={AngleRightIcon} />
  );
};

const FacetContainer = (props: any) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const borderColor = {
    dark: 'gray:60',
    light: 'gray:30',
  }[colorMode];
  return (
    <Flex
      backgroundColor={
        colorMode === 'dark'
          ? colorStyle.background.secondary
          : colorStyle.background.primary
      }
      borderRight="1px solid"
      borderRightColor={borderColor}
      height="100%"
      {...props}
    />
  );
};

export const LogFacet = React.memo(
  ({
    allFacets,
    facets,
    setFacets,
    isLoading,
    facetsOrder,
  }: {
    allFacets: AllFacets;
    facets: SearchItem[];
    setFacets: (...args: any) => any;
    isLoading: boolean;
    facetsOrder: string[];
  }) => {
    const debouncedSetFacets = useCallback(debounce(setFacets, INTERVAL), []);
    const { t } = useTranslation();
    const { getOrgName } = useOrgStore();
    const [allTreeData, setAllTreeData] = useState<DataNode[]>([]);
    const [treeData, setTreeData] = useState<DataNode[]>([]);
    const [viewMoreStatus, setViewMoreStatus] = useState<ViewMoreStatus>({});
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([
      facetsOrder[0],
    ]);
    const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);
    // const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
    const [autoExpandParent, setAutoExpandParent] = useState<boolean>(true);
    const [colorMode] = useColorMode();
    const [colorStyle] = useColorStyle({ colorMode });

    const onExpand = (expandedKeysValue: React.Key[]) => {
      //console.log('onExpand', expandedKeysValue);
      // if not set autoExpandParent to false, if children expanded, parent can not collapse.
      // or, you can remove all expanded children keys.
      setExpandedKeys(expandedKeysValue);
      setAutoExpandParent(false);
    };
    // onCheck use throttling to retrieve facets
    const onCheck = (checkedKeysValue: React.Key[]) => {
      const tmpFacets: { [key: string]: string[] } = {};
      checkedKeysValue.forEach((item) => {
        // only handler the sub node
        if (item.toString().includes(DELIMITER)) {
          const [field, value] = item.toString().split(DELIMITER);
          if (tmpFacets[field]) {
            tmpFacets[field] = [...tmpFacets[field], value];
          } else {
            tmpFacets[field] = [value];
          }
        }
      });
      const facets: SearchItem[] = Object.keys(tmpFacets).map((item) => {
        return {
          field: item,
          values: tmpFacets[item],
          type: 'terms',
        };
      });
      setCheckedKeys(checkedKeysValue);
      debouncedSetFacets(facets);
    };

    const onSelect = useCallback(
      //eslint-disable-next-line
      (selectedKeysValue: React.Key[], info: any) => {
        const newViewMoreStatus = { ...viewMoreStatus };
        if (~selectedKeysValue[0].toString().indexOf(DELIMITER)) {
          const [facet, index] = selectedKeysValue.toString().split(DELIMITER);
          newViewMoreStatus[facet] = parseInt(index) + 10;
          setViewMoreStatus(newViewMoreStatus);
        }
      },
      [viewMoreStatus]
    );
    const fieldFuncMap = {
      ddaaasRiskLevel: (facet: any) => {
        const facetName = 'ddaaasRiskLevel';
        const unratedStr = t(`quarantine.${facetName}.Unrated`);
        const unrantedNode: DataNode = {
          key: `${facetName}${DELIMITER}Unrated`,
          title: '',
        };
        let unratedCount = 0;
        let total = 0;
        const children: DataNode[] = [];
        facet.forEach((item: any) => {
          const { key, count } = item;
          const title = t(`quarantine.${facetName}.${key}`);
          if (title === unratedStr) {
            unratedCount += count;
          } else {
            const subNode: DataNode = {
              title: <SubFacetTitle title={title} count={count} />,
              key: `${facetName}${DELIMITER}${key}`,
            };
            children.push(subNode);
          }
          total += count;
        });
        if (unratedCount > 0) {
          unrantedNode.title = (
            <SubFacetTitle title={unratedStr} count={unratedCount} />
          );
          children.push(unrantedNode);
        }
        return { children, total };
      },
      dlpTemplate: (facet: any) => {
        const facetName = 'dlpTemplate';
        const children: DataNode[] = [];
        facet.forEach((item: any) => {
          const { key, subKey, count } = item;
          let title = key;
          if (
            t(`dlp.ENTITY.${subKey}.DISPLAYNAME`) !==
            `dlp.ENTITY.${subKey}.DISPLAYNAME`
          ) {
            title = t(`dlp.ENTITY.${subKey}.DISPLAYNAME`);
          }
          const subNode: DataNode = {
            title: <SubFacetTitle title={title} count={count} />,
            key: `${facetName}${DELIMITER}${key}${DELIMITER}${subKey}`,
          };
          children.push(subNode);
        });
        return { children, total: children.length };
      },
    };
    const handleFacetFieldTitle = (facetName: string, key: string) => {
      let showName = key;
      if (urlMaskFields.includes(facetName)) {
        showName = urlMask(key);
      } else if (facetName === 'companyGuid') {
        showName = getOrgName(key);
      } else if (mappingFields.includes(facetName as any)) {
        showName = getMappingFieldsName(facetName, key);
      }
      return showName;
    };
    const compareFacet = (a: DataNode, b: DataNode) => {
      return (
        facetsOrder.indexOf(a.key as string) -
        facetsOrder.indexOf(b.key as string)
      );
    };
    const compareFacetBucket = (a: any, b: any) => {
      return b.count - a.count;
    };
    // convert facets to tree data
    const convertFacet = (
      allFacets: any
    ): { allTreeData: DataNode[]; viewMoreStatus: ViewMoreStatus } => {
      if (allFacets) {
        const viewMoreStatus: ViewMoreStatus = {};
        const treeData: DataNode[] = [];
        Object.keys(allFacets).forEach((facetName) => {
          if (facetsOrder.includes(facetName)) {
            const facet = allFacets[facetName];
            facet.sort(compareFacetBucket);
            const node: DataNode = {
              key: facetName,
              title: facetName,
            };
            let children: DataNode[] = [];
            // facetName in wrapFieldFunc
            if (Object.keys(fieldFuncMap).includes(facetName)) {
              const { children: facetChildren } =
                fieldFuncMap[facetName as keyof typeof fieldFuncMap](facet);
              children = facetChildren;
            } else {
              // eslint-disable-next-line @typescript-eslint/prefer-for-of
              for (const child of facet) {
                const { key, count } = child;
                const subNode: DataNode = {
                  title: key,
                  key: `${facetName}${DELIMITER}${key}`,
                };
                const showName = handleFacetFieldTitle(facetName, key);
                subNode.title = (
                  <SubFacetTitle title={showName} count={count} />
                );
                children.push(subNode);
              }
            }

            if (children.length) {
              node.children = children;
            }
            node.title = (
              <MainFacetTitle title={t(`log.fields.${facetName}`)} />
            );
            viewMoreStatus[facetName] = facet.length > 10 ? 10 : 0;
            treeData.push(node);
          }
        });
        treeData.sort(compareFacet);
        return {
          allTreeData: treeData,
          viewMoreStatus,
        };
      } else {
        return {
          allTreeData: [],
          viewMoreStatus: {},
        };
      }
    };
    const getShowTreeData = useCallback(
      (allTreeData: DataNode[], viewMoreStatus: ViewMoreStatus) => {
        const showTreeData: DataNode[] = [];
        const viewMoreTitle = t(`log.button.viewMore`);
        for (const node of allTreeData) {
          const newNode = { ...node };
          const { key } = node;
          const index = viewMoreStatus[key as string | number];
          if (index !== 0) {
            const { children } = node;
            if (children && children.length > index) {
              const viewMore = viewMoreNode(
                key as string,
                index,
                viewMoreTitle
              );
              const newChildren = [...children.slice(0, index), viewMore];
              newNode.children = newChildren;
            } else {
              // do not need show view more
            }
            showTreeData.push(newNode);
          } else {
            showTreeData.push(newNode);
          }
        }
        return showTreeData;
      },
      []
    );
    const onClearAll = useCallback(() => {
      const facets: SearchItem[] = [];
      debouncedSetFacets.cancel();
      setFacets(facets);
      setCheckedKeys([]);
    }, []);
    //console.log('LogFacet render');
    useEffect(() => {
      const { allTreeData, viewMoreStatus } = convertFacet(allFacets);
      setAllTreeData(allTreeData);
      setViewMoreStatus(viewMoreStatus);
    }, [allFacets]);

    useEffect(() => {
      const showTreeData = getShowTreeData(allTreeData, viewMoreStatus);
      setTreeData(showTreeData);
    }, [viewMoreStatus, allTreeData]);

    useEffect(() => {
      const checkedKeys: React.Key[] = [];
      facets.forEach((selectedFacet) => {
        const { field, values } = selectedFacet;
        const facet = allFacets[field];
        if (Array.isArray(facet)) {
          facet.forEach((item) => {
            if (values.includes(item.key)) {
              checkedKeys.push(`${field}${DELIMITER}${item.key}`);
            }
          });
        }
      });
      setCheckedKeys(checkedKeys);
    }, [facets, allFacets]);
    // tree's onSelect set viewMoreStatus
    // useEffect listen to viewMoreStatus and set showTreeData

    return (
      <FacetContainer>
        <Scrollbar width="320px" height="100%" maxHeight="100%">
          <Flex direction="column" height="100%">
            <Flex justify="space-between">
              <Text color={colorStyle.color.secondary} pl="20px" pt="22px">
                {t('log.hint.searchFilter')}
              </Text>
              {facets.length || checkedKeys.length ? (
                <Box
                  color="#6F9BF4"
                  onClick={onClearAll}
                  pr="20px"
                  pt="22px"
                  cursor="pointer"
                >
                  {t(`log.button.clearAll`)}
                </Box>
              ) : null}
            </Flex>
            {isLoading ? (
              <Spinner margin="auto" />
            ) : (
              <Tree
                checkable
                onExpand={onExpand}
                expandedKeys={expandedKeys}
                autoExpandParent={autoExpandParent}
                onCheck={onCheck as TreeProps['onCheck']}
                checkedKeys={checkedKeys}
                onSelect={onSelect}
                // selectedKeys={selectedKeys}
                treeData={treeData}
                switcherIcon={SwitchIcon}
                rootStyle={{
                  paddingTop: '6px',
                  background: 'transparent',
                }}
              />
            )}
          </Flex>
        </Scrollbar>
      </FacetContainer>
    );
  }
);
